import { mdiArrowCollapseDown, mdiArrowCollapseUp, mdiFormatListBulleted, mdiFormatListNumbered } from '@mdi/js';
import type { Transaction } from '@tiptap/pm/state';

import { OrderedListDecimal } from '../components/icons/OrderedListDecimal';
import { OrderedListLetters } from '../components/icons/OrderedListLetters';
import { OrderedListRoman } from '../components/icons/OrderedListRoman';
import type { Option } from '../types';
import { OrderedListType } from '../types';

import { getSeparator } from './separator';

const updateCurrentList = (editor: any, newStart: number) => {
  const { state } = editor;
  const { selection } = state;
  const { $from } = selection;
  let depth = $from.depth;
  let nestedListPos: number | null = null;

  while (depth > 0) {
    const node = $from.node(depth);
    if (node.type.name === 'orderedList') {
      const parent = $from.node(depth - 1);
      if (parent?.type.name === 'listItem') {
        nestedListPos = $from.before(depth);
        break;
      }
    }
    depth--;
  }

  const newAttributes = { start: newStart };

  if (nestedListPos === null) {
    editor.commands.updateAttributes('orderedList', newAttributes);
  } else {
    editor.commands.command(({ tr, dispatch }: { tr: Transaction; dispatch: (tr: Transaction) => void }) => {
      if (typeof nestedListPos === 'number') {
        tr.setNodeMarkup(nestedListPos, undefined, {
          ...tr.doc.nodeAt(nestedListPos)?.attrs,
          ...newAttributes,
        });
      }
      dispatch(tr);
      return true;
    });
  }
};

export const getListControls = (editor: any): Option[] => {
  if (!editor) {
    return [];
  }

  const setOrderedList = (listType: OrderedListType) => {
    const isOrderedList = editor.isActive('orderedList');

    if (!isOrderedList) {
      editor.chain().focus().toggleOrderedList().run();
    }

    editor.chain().focus().setIconType(listType).run();
  };

  return [
    {
      format: () => editor?.chain().focus().toggleBulletList().run(),
      isActive: editor?.isActive('bulletList'),
      icon: mdiFormatListBulleted,
      key: 'bulletList',
      title: 'Bulleted list',
    },
    {
      format: () => setOrderedList(OrderedListType.NUMBERS),
      isActive: editor.getAttributes('orderedList').type === OrderedListType.NUMBERS,
      icon: mdiFormatListNumbered,
      title: 'Ordered list',
      key: 'ordered-list-numbers',
    },
    {
      format: () => setOrderedList(OrderedListType.DECIMAL),
      isActive: editor.getAttributes('orderedList').type === OrderedListType.DECIMAL,
      icon: OrderedListDecimal,
      title: 'Decimal',
      key: 'ordered-list-decimal',
    },
    {
      format: () => setOrderedList(OrderedListType.LETTERS),
      isActive: editor.getAttributes('orderedList').type === OrderedListType.LETTERS,
      icon: OrderedListLetters,
      title: 'Letters',
      key: 'ordered-list-letters',
    },
    {
      format: () => setOrderedList(OrderedListType.ROMAN),
      isActive: editor.getAttributes('orderedList').type === OrderedListType.ROMAN,
      icon: OrderedListRoman,
      title: 'Roman literals',
      key: 'ordered-list-roman',
    },
    getSeparator('lists-2'),
    {
      format: () => {
        const currentAttrs = editor.getAttributes('orderedList');
        const currentStart = currentAttrs.start || 1;
        const newStart = Math.max(1, currentStart - 1);
        updateCurrentList(editor, newStart);
      },
      icon: mdiArrowCollapseDown,
      key: 'decreaseStart',
      title: 'Decrease Start',
    },
    {
      format: () => {
        const currentAttrs = editor.getAttributes('orderedList');
        const currentStart = currentAttrs.start || 1;
        const newStart = currentStart + 1;
        updateCurrentList(editor, newStart);
      },
      icon: mdiArrowCollapseUp,
      key: 'increaseStart',
      title: 'Increase Start',
    },
  ];
};
